import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import DataGrid from 'components/common/data-grid/data-grid-component';
import { DataGridDataApi } from 'models/utils/common/data-grid/data-grid-data-model';
import Modal from 'components/common/modal-component';
import { ModalButton } from 'models/utils/common/modal-button-model';
import { attachTickmarkListColumns } from 'constants/feature/statement-content-constants';
import { batchUpdateElementRequest } from 'api/element-api';
import BatchElementUpdateForm from 'models/form/batch-element-update-form-model';
import { updateElementCacheByCalloutAction } from 'store/actions/element-cache-actions';
import { fetchTickmarkListForRevision } from 'store/actions/tickmark-list-panel-actions';
import { LEFT_PANELS } from 'constants/feature/panel-constants';
import { StatusCodes } from 'http-status-codes';

export const ATTACH_EXISTING_TICKMARK_BLOCK = 'attach-existing-tickmark';

const AttachExistingTickmarkModal = ({
  onClose,
  listData,
  elementIds,
  revisionId,
}) => {
  const [selectedRows, setSelectedRows] = useState({});
  const [updates, setUpdates] = useState(new BatchElementUpdateForm());
  const leftPanel = useSelector((state) => state.ui.statementPage.panels.left);
  const socketHasBeenDisconnected = useSelector(
    (state) => state.sockets.statementSocket.socketHasBeenDisconnected,
  );
  const isLeftTickmarkpanelActive = leftPanel === LEFT_PANELS.TICKMARK;

  const onRowCheckboxSelect = (selectedRows) => {
    setSelectedRows(selectedRows);

    setUpdates((prevUpdates) => {
      let tickmarks = [];
      for (let key in selectedRows) {
        if (selectedRows[key]) {
          tickmarks.push({ tickmarkId: parseInt(key) });
        }
      }
      prevUpdates = prevUpdates.resetTickmarks();
      return prevUpdates.setTickmarkList(tickmarks);
    });
  };

  const onSubmit = async () => {
    try {
      onClose();
      const response = await batchUpdateElementRequest({
        elementIds,
        updatesModel: updates,
        revisionId,
      });
      if (response.status === StatusCodes.OK) {
        updateElementCacheByCalloutAction({
          elementIds,
          showCallout: true,
        });

        if (isLeftTickmarkpanelActive) {
          if (updates.shouldAddTickmarks && socketHasBeenDisconnected) {
            fetchTickmarkListForRevision({
              revisionId,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error attaching tickmarks in batch:', error);    
    }
  };

  const isAnyRowSelected = Object.values(selectedRows).some(Boolean);

  return (
    <Modal
      id={`${ATTACH_EXISTING_TICKMARK_BLOCK}`}
      title={'quick-actions.attach-existing-tickmark-modal.title'}
      primaryModalButton={
        new ModalButton({
          text: 'common.save',
          onClick: onSubmit,
          disabled: !isAnyRowSelected,
        })
      }
      secondaryModalButton={
        new ModalButton({
          text: 'common.cancel',
          onClick: onClose,
        })
      }
      onClose={onClose}
    >
      <div>
        <DataGrid
          className={classnames(`${ATTACH_EXISTING_TICKMARK_BLOCK}__list`)}
          columns={attachTickmarkListColumns}
          tableId={`${ATTACH_EXISTING_TICKMARK_BLOCK}-list`}
          scrollable={true}
          numberOfVisibleRows={2.5}
          dataModel={
            new DataGridDataApi({
              apiModel: { isLoaded: true },
              rowItems: listData.map((tm) => ({ ...tm, id: tm.tickmarkId })),
            })
          }
          onRowCheckboxSelect={onRowCheckboxSelect}
          selectedRows={selectedRows}
          canSelectMoreThenOneRow={true}
        />
      </div>
    </Modal>
  );
};

export default AttachExistingTickmarkModal;

AttachExistingTickmarkModal.propTypes = {
  /** function fired to close the modal */
  onClose: PropTypes.func.isRequired,
  /** function fired to submit acknowledgement */
  onSubmit: PropTypes.func.isRequired,
};
